import React, { useState, useEffect } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";

export default function Invoice({ taskData = null, taskId = null, hideControls = false }) {
  const { http, user } = AuthUser();
  const [invoiceData, setInvoiceData] = useState({
    // Top fields
    contactPerson: "",
    phone: "",
    email: "",
    location: "",
    
    // Customer section
    personName: "",
    customerCompany: "",
    customerPhone: "",
    customerEmail: "",
    customerLocation: "",
    
    // Product section
    productName: "",
    assignedTo: "",
    serviceTime: "",
    establishTime: "",
    problemDescription: "",
    targetDate: "",
    priority: "Medium",
    serviceAmount: "",
    
    // AMC & System section
    isAmc: false,
    systemSuppliedBy: "",
    appxWhenSupplied: "",
    whoServicedBefore: "",
    
    // Solution & Pricing section
    recommendedSolution: "",
    pricingType: "Regular",
    quoteSubmit: "",
    invoiceGenerated: false,
    paymentCollectImmediate: false,
    invoiceWhoToGive: "",
    finalCustomerAvailabilityChecked: false,
    messageToCustomer: "",
    
    // Job card number
    job_card_number: null
  });

  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);

  // Load task data when taskId is provided
  useEffect(() => {
    if (taskId && !taskData) {
      fetchTaskInvoiceData();
    } else if (taskData) {
      populateInvoiceData(taskData);
    }
  }, [taskId, taskData]);

  const fetchTaskInvoiceData = async () => {
    setLoading(true);
    try {
      const response = await http.get(`/task_invoice_data?id=${taskId}`);
      if (response.data.data) {
        populateInvoiceData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching task data:", error);
      toast.error("Error loading task data");
    } finally {
      setLoading(false);
    }
  };

  const populateInvoiceData = (task) => {
    setInvoiceData({
      // Job card number
      job_card_number: task.job_card_number || null,
      
      // Top fields
      contactPerson: task.customer?.name || "",
      phone: task.customer?.phone || "",
      email: task.customer?.email || "",
      location: task.location || "",
      
      // Customer section
      personName: task.customer?.name || "",
      customerCompany: task.customer?.company || "",
      customerPhone: task.customer_phone || task.customer?.phone || "",
      customerEmail: task.customer?.email || "",
      customerLocation: task.customer?.address || "",
      
      // Product section
      productName: task.product_name || "",
      assignedTo: task.assignee ? `${task.assignee.name} ${task.assignee.last_name}` : "",
      serviceTime: task.when_to_go || "",
      establishTime: task.est_time || "",
      problemDescription: task.description || "",
      targetDate: task.target_date || "",
      priority: task.priority || "Medium",
      serviceAmount: task.service_amount || "",
      
      // AMC & System section
      isAmc: Boolean(task.is_amc),
      systemSuppliedBy: task.system_supplied_by || "",
      appxWhenSupplied: task.supplied_year || "",
      whoServicedBefore: task.who_serviced_before || "",
      
      // Solution & Pricing section
      recommendedSolution: task.recommended_solution || "",
      pricingType: task.pricing_type || "Regular",
      quoteSubmit: task.quote_details || "",
      invoiceGenerated: Boolean(task.invoice_generated),
      paymentCollectImmediate: Boolean(task.payment_collect_immediate),
      invoiceWhoToGive: task.invoice_who_to_give || "",
      finalCustomerAvailabilityChecked: Boolean(task.final_customer_availability_checked),
      messageToCustomer: task.message_to_customer || "",
      
      // Job card number
      job_card_number: task.job_card_number || null
    });

    if (task.created_at) {
      setDate(moment(task.created_at).format("YYYY-MM-DD"));
    }
  };

  const handleInputChange = (field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="container-fluid mt-2">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading invoice data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster />
      <div className="container-fluid mt-2">
        <div className="d-flex justify-content-between align-items-center mb-2 no-print">
          <h3 className="text-primary mb-0">Customer Job Card</h3>
          {!hideControls && (
            <button className="btn btn-primary btn-sm" onClick={handlePrint}>
              🖨️ Print
            </button>
          )}
        </div>

        <div className="invoice-container">
          {/* Header */}
          <div className="invoice-header">
            <div className="header-content">
              <div className="logo-section">
                <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8dGV4dCB4PSI1MCIgeT0iMjUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyMCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IiMwMDdiZmYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPktBVFpFPC90ZXh0Pgo8L3N2Zz4K" alt="KATZE Logo" className="katze-logo" />
              </div>
              <div className="title-section">
                <h3>📋 KATZE JOB CARD</h3>
                <p>📅 Date: {moment(date).format("DD/MM/YYYY")} {invoiceData.job_card_number && <span className="job-id-highlight">| 🆔 Job ID: {invoiceData.job_card_number}</span>}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="invoice-content">
            
            {/* 1. Customer Availability and Assigned To in same row */}
            <div className="top-row-header">
              <div className="customer-availability-header">
                <strong>✅ Customer Availability:</strong>
                <label className="availability-option"><input type="checkbox" /> Yes</label>
                <label className="availability-option"><input type="checkbox" /> No</label>
              </div>
              <div className="assigned-to-header">
                <strong>👤 To: <span className="assigned-to-value">{invoiceData.assignedTo}</span></strong>
              </div>
            </div>

            {/* Customer Section */}
                          <div className="field-group" style={{ marginBottom: '10px' }}>
                <h4 className="group-title" style={{ marginBottom: '8px' }}>👥 Customer:</h4>
              <div className="field-row-split" style={{ gap: '20px' }}>
                <div className="field-half">
                  <span className="field-label">Person Name:</span>
                  <input type="text" className="field-input" value={invoiceData.personName} onChange={(e) => handleInputChange('personName', e.target.value)} placeholder="" />
                </div>
                <div className="field-half">
                  <span className="field-label">Company:</span>
                  <input type="text" className="field-input" value={invoiceData.customerCompany} onChange={(e) => handleInputChange('customerCompany', e.target.value)} placeholder="" />
                </div>
              </div>
              {/* Phone and Email in same row */}
              <div className="field-row-split">
                <div className="field-half">
                  <span className="field-label">Phone:</span>
                  <input type="text" className="field-input bold-phone" value={invoiceData.customerPhone} onChange={(e) => handleInputChange('customerPhone', e.target.value)} placeholder="" />
                </div>
                <div className="field-half">
                  <span className="field-label">Email:</span>
                  <input type="email" className="field-input" value={invoiceData.customerEmail} onChange={(e) => handleInputChange('customerEmail', e.target.value)} placeholder="" />
                </div>
              </div>
              {/* Location in separate row for more space */}
              <div className="field-row">
                <span className="field-label">Location:</span>
                <input type="text" className="field-input" value={invoiceData.customerLocation} onChange={(e) => handleInputChange('customerLocation', e.target.value)} placeholder="" />
              </div>
            </div>

            {/* Product Section */}
                          <div className="field-group" style={{ marginBottom: '10px' }}>
                <h4 className="group-title" style={{ marginBottom: '8px' }}>📦 Product Name:</h4>
              <div className="field-row">
                <span className="field-label">Product Name:</span>
                <input type="text" className="field-input" value={invoiceData.productName} onChange={(e) => handleInputChange('productName', e.target.value)} placeholder="" />
                  </div>
              <div className="field-row-split">
                <div className="field-half">
                  <span className="field-label">Assigned By:</span>
                  <input type="text" className="field-input" value={invoiceData.assignedTo} onChange={(e) => handleInputChange('assignedTo', e.target.value)} placeholder="" />
                </div>
                <div className="field-half">
                  <span className="field-label">Service Time:</span>
                  <input type="text" className="field-input" value={invoiceData.serviceTime} onChange={(e) => handleInputChange('serviceTime', e.target.value)} placeholder="" />
                </div>
              </div>
              {/* Establish Time and Target Date in same row */}
              <div className="field-row-split">
                <div className="field-half">
                  <span className="field-label">Establish Time:</span>
                  <input type="text" className="field-input" value={invoiceData.establishTime} onChange={(e) => handleInputChange('establishTime', e.target.value)} placeholder="" />
                </div>
                <div className="field-half">
                  <span className="field-label">Target Date:</span>
                  <input type="date" className="field-input" value={invoiceData.targetDate} onChange={(e) => handleInputChange('targetDate', e.target.value)} />
                </div>
              </div>
              <div className="field-row problem-description-row">
                <span className="field-label bold-field"><strong>Problem & Description:</strong></span>
                <textarea className="field-textarea bold-value large-text" value={invoiceData.problemDescription} onChange={(e) => handleInputChange('problemDescription', e.target.value)} placeholder="" rows="4" style={{ width: '100%', minHeight: '80px', fontSize: '16px' }}></textarea>
              </div>

              <style>
                {`
                  .field-input, .field-textarea {
                    font-size: 16px !important;
                    color: #0066cc !important;
                  }
                  
                  .field-label {
                    font-size: 15px !important;
                  }

                  input[type="text"].bold-phone {
                    font-size: 18px !important;
                    font-weight: bold !important;
                  }

                  @media print {
                    .field-input, .field-textarea {
                      font-size: 14px !important;
                      white-space: pre-wrap !important;
                      overflow: visible !important;
                    }
                    
                    .problem-description-row .field-textarea {
                      min-height: auto !important;
                      height: auto !important;
                      overflow: visible !important;
                    }
                  }
                `}
              </style>
              {/* Priority and Service Amount in same row */}
              <div className="field-row-split">
                <div className="field-half">
                  <span className="field-label">Priority:</span>
                  <span className="field-value">
                    <label><input type="radio" name="priority" checked={invoiceData.priority === "High"} onChange={() => handleInputChange('priority', 'High')} /> High</label>
                    <label><input type="radio" name="priority" checked={invoiceData.priority === "Medium"} onChange={() => handleInputChange('priority', 'Medium')} /> Medium</label>
                    <label><input type="radio" name="priority" checked={invoiceData.priority === "Low"} onChange={() => handleInputChange('priority', 'Low')} /> Low</label>
                  </span>
                </div>
                <div className="field-half">
                  <span className="field-label">Service Amount:</span>
                  <input type="number" className="field-input" value={invoiceData.serviceAmount} onChange={(e) => handleInputChange('serviceAmount', e.target.value)} placeholder="" />
                </div>
              </div>
            </div>

            {/* AMC & System Section */}
            <div className="field-group" style={{ marginBottom: '10px' }}>
              {/* AMC Status back to original style */}
              <div className="amc-status-section" style={{ marginBottom: '8px' }}>
                <div className="amc-title">📋 AMC Status:</div>
                <div className="amc-options">
                  <label className="amc-option"><input type="radio" name="amc" checked={invoiceData.isAmc === true} onChange={() => handleInputChange('isAmc', true)} /> <strong>Yes</strong></label>
                  <label className="amc-option"><input type="radio" name="amc" checked={invoiceData.isAmc === false} onChange={() => handleInputChange('isAmc', false)} /> <strong>No</strong></label>
                </div>
              </div>

              {/* System fields in one row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                {/* System Supplier */}
                <div style={{ display: 'flex', alignItems: 'center', flex: 2 }}>
                  <span className="field-label" style={{ marginRight: '10px', whiteSpace: 'nowrap' }}>System Supplier:</span>
                  <input type="text" className="field-input" value={invoiceData.systemSuppliedBy} onChange={(e) => handleInputChange('systemSuppliedBy', e.target.value)} placeholder="" style={{ flex: 1 }} />
                </div>

                {/* Supply Year */}
                <div style={{ display: 'flex', alignItems: 'center', width: '200px' }}>
                  <span className="field-label" style={{ marginRight: '10px', whiteSpace: 'nowrap' }}>Supply Year:</span>
                  <input type="text" className="field-input" value={invoiceData.appxWhenSupplied} onChange={(e) => handleInputChange('appxWhenSupplied', e.target.value)} placeholder="" style={{ width: '80px' }} />
                </div>

                {/* Previous Service */}
                <div style={{ display: 'flex', alignItems: 'center', flex: 2 }}>
                  <span className="field-label" style={{ marginRight: '10px', whiteSpace: 'nowrap' }}>Previous Service:</span>
                  <input type="text" className="field-input" value={invoiceData.whoServicedBefore} onChange={(e) => handleInputChange('whoServicedBefore', e.target.value)} placeholder="" style={{ flex: 1 }} />
                </div>
              </div>
            </div>

            {/* Solution Section */}
                          <div className="field-group" style={{ marginBottom: '10px' }}>
                <div className="field-row">
                  <span className="field-label">💡 Recommended Solution:</span>
                  <textarea className="field-textarea" value={invoiceData.recommendedSolution} onChange={(e) => handleInputChange('recommendedSolution', e.target.value)} placeholder="" rows="2" style={{ minHeight: '45px' }}></textarea>
                </div>
              </div>

            {/* Pricing & Final Section */}
            <div className="field-group">
              <div className="field-row-triple" style={{ alignItems: 'center', gap: '5px' }}>
                <div className="field-third">
                  <span className="field-label">💰 Pricing:</span>
                  <span className="field-value" style={{ display: 'inline-flex', gap: '10px', marginLeft: '10px' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      <input type="radio" name="pricing" checked={invoiceData.pricingType === "Regular"} onChange={() => handleInputChange('pricingType', 'Regular')} />
                      Regular
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      <input type="radio" name="pricing" checked={invoiceData.pricingType === "Customized"} onChange={() => handleInputChange('pricingType', 'Customized')} />
                      Custom
                    </label>
                  </span>
                </div>

                <div className="field-third">
                  <span className="field-label">Payment:</span>
                  <span className="field-value" style={{ display: 'inline-flex', gap: '10px', marginLeft: '10px' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      <input type="radio" name="payment" checked={invoiceData.paymentCollectImmediate === true} onChange={() => handleInputChange('paymentCollectImmediate', true)} />
                      Immediate
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      <input type="radio" name="payment" checked={invoiceData.paymentCollectImmediate === false} onChange={() => handleInputChange('paymentCollectImmediate', false)} />
                      Credit
                    </label>
                  </span>
                </div>

                <div className="field-third">
                  <span className="field-label">Invoice To:</span>
                  <input type="text" className="field-input" value={invoiceData.invoiceWhoToGive} onChange={(e) => handleInputChange('invoiceWhoToGive', e.target.value)} placeholder="" style={{ marginLeft: '10px', width: 'calc(100% - 80px)' }} />
                </div>
              </div>

            </div>

            {/* 6. Bottom table */}
            <div className="">
              <table className="task-table">
                <thead>
                  <tr>
                    <th style={{ width: '1%', whiteSpace: 'nowrap', padding: '4px' }}>SR NO</th>
                    <th className="task-col" style={{ width: '80%' }}>TASK</th>
                    <th className="remark-col" style={{ width: '20%' }}>REMARK</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ height: '40px' }}><td>1</td><td></td><td></td></tr>
                  <tr style={{ height: '40px' }}><td>2</td><td></td><td></td></tr>
                  <tr style={{ height: '40px' }}><td>3</td><td></td><td></td></tr>
                  <tr style={{ height: '40px' }}><td>4</td><td></td><td></td></tr>
                </tbody>
              </table>
              
              {/* Main bottom section with Pending Tasks, Internal, and Sign & Seal */}
              <div className="bottom-two-columns" style={{ display: 'flex', gap: '20px', marginBottom: '3px' }}>
                <div className="left-column" style={{ flex: '2.3' }}>
                  {/* Pending Tasks */}
                  <div className="pending-tasks-row" style={{ marginBottom: '3px' }}>
                    <span className="field-label" style={{ fontSize: '16px', display: 'block', marginBottom: '3px', letterSpacing: '0.5px', fontWeight: 'bold' }}>Pending Tasks (if any):</span>
                    <input type="text" className="field-input" placeholder="" style={{ 
                      height: '35px',
                      fontSize: '14px',
                      width: '100%'
                    }} />
                  </div>
                  
                  {/* Internal section moved here */}
                  <div className="internal-section">
                    <h4 style={{ marginBottom: '3px', fontSize: '16px' }}><strong>Internal:</strong></h4>
                    <table className="internal-table" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th className="invoice-status-col">Invoice Status</th>
                          <th>Quote Details</th>
                          <th>Column 3</th>
                          <th>Column 4</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="right-column" style={{ flex: '1' }}>
                  {/* Customer Sign & Seal box */}
                  <div className="signature-box-container" style={{ width: '100%' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ 
                        fontSize: '15px',
                        fontWeight: 'bold',
                        marginBottom: '15px',
                        color: '#2c3e50'
                      }}>
                        ✍️ Customer Sign & Seal
                      </div>
                      <div style={{ 
                        minHeight: '100px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <div style={{ color: '#666', fontSize: '14px', marginBottom: '25px' }}>Sign Here</div>
                        <div style={{ 
                          borderTop: '2px dashed #999', 
                          width: '90%',
                          position: 'relative',
                          top: '-10px'
                        }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

             

              <style>
                {`
                  @media print {
                    .field-group {
                      margin-bottom: 8px !important;
                    }
                    .group-title {
                      margin-bottom: 5px !important;
                    }
                    .field-row, .field-row-split, .field-row-triple {
                      margin-bottom: 5px !important;
                    }
                    textarea.field-textarea {
                      min-height: 45px !important;
                    }
                    .task-table {
                      margin: 5px 0 !important;
                    }
                    .task-table td {
                      padding: 4px 8px !important;
                    }
                    .bottom-two-columns {
                      margin: 5px 0 !important;
                    }
                    .signature-area {
                      min-height: 80px !important;
                    }
                    .internal-section {
                      margin-top: 5px !important;
                    }
                    .internal-table td {
                      padding: 4px 8px !important;
                    }
                  }

                  .signature-box-outline {
                    border: 2px solid #e0e0e0;
                    border-radius: 6px;
                    overflow: hidden;
                  }
                  .signature-box-header {
                    background: #f8f9fa;
                    border-bottom: 2px solid #e0e0e0;
                    font-weight: bold;
                    font-size: 15px;
                    color: #2c3e50;
                    padding: '8px 15px';
                  }
                  .signature-box-content {
                    padding: 10px;
                    background: #ffffff;
                  }
                  .signature-area {
                    border: 2px dashed #ccc;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fafbfc;
                    min-height: 60px;
                  }
                  .signature-placeholder {
                    color: #aaa;
                    font-size: 14px;
                    font-style: italic;
                  }
                  @media print {
                    .signature-box-outline {
                      border: 2px solid #000 !important;
                    }
                    .signature-box-header {
                      border-bottom: 2px solid #000 !important;
                      background: #fff !important;
                    }
                    .signature-area {
                      border: 2px dashed #000 !important;
                      background: #fff !important;
                    }
                  }
                `}
              </style>
              
             
            </div>

          </div>
        </div>
      </div>

      <style jsx>{`
        .invoice-container {
          max-width: 100%;
          margin: 0 auto;
          background: white;
          border: 1px solid #ddd;
          padding: 20px;
        }

        .invoice-header {
          text-align: center;
          margin-bottom: 6px;
          padding-bottom: 15px;
          border-bottom: 2px solid #333;
        }

        .invoice-header h3 {
          margin: 0 0 10px 0;
          font-size: 24px;
          color: #333;
        }

        .invoice-header p {
          margin: 0;
          font-size: 16px;
          color: #666;
        }

        .job-id-highlight {
          background: linear-gradient(135deg, #007bff, #0056b3);
          color: white;
          padding: 4px 12px;
          border-radius: 20px;
          font-weight: bold;
          font-size: 14px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
          border: 2px solid #ffffff;
          display: inline-block;
          margin-left: 8px;
          letter-spacing: 0.5px;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { 
            transform: scale(1); 
            box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
          }
          50% { 
            transform: scale(1.05); 
            box-shadow: 0 4px 12px rgba(0, 123, 255, 0.5);
          }
        }

        .invoice-content {
          line-height: 1.6;
        }

        .field-group {
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 5px;
          background: #fafafa;
        }

        .group-title {
          font-size: 18px;
          font-weight: bold;
          color: #333;
          margin: 0 0 15px 0;
          padding-bottom: 8px;
          border-bottom: 1px solid #ccc;
        }

        .field-row {
          margin-bottom: 15px;
          display: block;
          clear: both;
        }

        .field-label {
          display: inline-block;
          width: 200px;
          font-weight: bold;
          color: #333;
          vertical-align: top;
          font-size: 14px;
        }

        .field-input {
          display: inline-block;
          width: calc(100% - 220px);
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
        }

        .field-textarea {
          display: inline-block;
          width: 100%;
          max-width: calc(100% - 220px);
          min-width: 300px;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
          resize: vertical;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .field-value {
          display: inline-block;
          vertical-align: top;
        }

        .field-value label {
          margin-right: 15px;
          font-weight: normal;
          cursor: pointer;
        }

        .field-value input[type="radio"] {
          margin-right: 5px;
        }

        /* Print Styles */
        @media print {
          .no-print {
            display: none !important;
          }
          
          body {
            margin: 0 !important;
            padding: 0 !important;
            font-size: 10px !important;
          }
          
          .invoice-container {
            border: none !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 10px !important;
            width: 100% !important;
            max-width: 100% !important;
            page-break-inside: avoid !important;
            box-sizing: border-box !important;
          }

          .invoice-header {
            margin-bottom: 10px !important;
            padding-bottom: 8px !important;
          }

          .invoice-header h3 {
            font-size: 14px !important;
            margin-bottom: 4px !important;
          }

          .invoice-header p {
            font-size: 10px !important;
            margin: 0 !important;
          }

          .job-id-highlight {
            background: #007bff !important;
            color: white !important;
            padding: 2px 8px !important;
            border-radius: 12px !important;
            font-weight: bold !important;
            font-size: 9px !important;
            border: 1px solid #0056b3 !important;
            display: inline-block !important;
            margin-left: 4px !important;
            letter-spacing: 0.3px !important;
            animation: none !important;
            text-shadow: none !important;
            box-shadow: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .invoice-content {
            line-height: 1.1 !important;
          }

          .field-group {
            border: 1px solid #000 !important;
            background: white !important;
            margin-bottom: 6px !important;
            padding: 6px !important;
            page-break-inside: avoid !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }

          .group-title {
            font-size: 10px !important;
            margin-bottom: 4px !important;
            padding-bottom: 2px !important;
          }

          .field-row {
            margin-bottom: 3px !important;
            display: flex !important;
            align-items: flex-start !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }

          .field-label {
            font-size: 8px !important;
            width: 120px !important;
            min-width: 120px !important;
            max-width: 120px !important;
            font-weight: bold !important;
            margin-right: 6px !important;
            flex-shrink: 0 !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }

          .field-input, .field-textarea {
            font-size: 8px !important;
            width: auto !important;
            flex: 1 !important;
            border: none !important;
            border-bottom: 1px solid #000 !important;
            padding: 1px 2px !important;
            background: transparent !important;
            min-height: 10px !important;
            max-width: calc(100% - 130px) !important;
            box-sizing: border-box !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }

          .field-textarea {
            min-height: 16px !important;
            resize: none !important;
            width: 100% !important;
            max-width: none !important;
            min-width: auto !important;
            word-break: break-word !important;
            white-space: pre-wrap !important;
          }

          .field-value {
            flex: 1 !important;
            font-size: 8px !important;
            max-width: calc(100% - 130px) !important;
            word-wrap: break-word !important;
          }

          .field-value label {
            margin-right: 6px !important;
            font-weight: normal !important;
            font-size: 8px !important;
            display: inline-block !important;
          }

          .field-value input[type="radio"] {
            margin-right: 2px !important;
            transform: scale(0.7) !important;
          }

          /* Prevent page breaks within important sections */
          .field-group:first-child {
            margin-top: 0 !important;
          }

          .field-group:last-child {
            margin-bottom: 0 !important;
          }

          /* Ensure no content overflows */
          * {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          @page {
            size: A4 portrait;
            margin: 0.4in 0.3in;
          }
        }

        /* 1. Customer Availability Header - Updated */
        .customer-availability-header {
          color: #333 !important;
          background: transparent !important;
          border: none !important;
          margin: 0 !important;
          padding: 0 !important;
          font-size: 14px;
        }
        
        .availability-option {
          margin-left: 15px;
          padding: 0 !important;
          background: transparent !important;
          border: none !important;
          border-radius: 0 !important;
          font-weight: normal;
          cursor: pointer;
          color: #333 !important;
        }

        /* Assigned To Header - Updated */
        .assigned-to-header {
          color: #333 !important;
          background: transparent !important;
          border: none !important;
          margin: 0 !important;
          padding: 0 !important;
          font-size: 14px;
        }

        /* 2. Bold Problem & Description */
        .bold-field {
          font-weight: bold !important;
        }
        
        .bold-value {
          font-weight: bold !important;
        }

        /* 3. AMC Status Highlight */
        .amc-highlight {
          background: #fff3cd;
          padding: 8px;
          border-radius: 4px;
          border: 2px solid #ffc107;
        }
        
        .amc-radio {
          background: #e7f3ff;
          padding: 5px 10px;
          margin: 0 5px;
          border-radius: 15px;
          border: 1px solid #007bff;
        }

        /* 6. Bottom Table Section */
        .bottom-table-section {
          margin-top: 30px;
          border-top: 2px solid #333;
          padding-top: 20px;
        }

        .task-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }

        .task-table th,
        .task-table td {
          border: 1px solid #333;
          padding: 8px;
          text-align: left;
        }

        .task-table th {
          background: #f8f9fa;
          font-weight: bold;
        }

        .task-table td {
          height: 40px;
        }

        .invoice-status-row,
        .pending-tasks-row,
        .signature-row {
          margin: 15px 0;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .signature-box {
          height: 40px;
          border: 1px dashed #999;
          margin-top: 10px;
          background: #fafafa;
        }

        /* 2. Internal Section */
        .internal-section {
          margin-top: 15px;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .internal-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }

        .internal-table th,
        .internal-table td {
          border: 1px solid #333;
          padding: 6px;
          text-align: center;
        }

        .internal-table th {
          background: #f8f9fa;
          font-weight: bold;
          font-size: 12px;
        }

        .internal-table td {
          height: 30px;
        }

        /* 3. Single Page Print Optimization */
        @media print {
          .invoice-container {
            font-size: 10px !important;
            padding: 5px !important;
            margin: 0 !important;
          }
          
          .customer-availability-header,
          .assigned-to-header {
            margin-bottom: 8px !important;
            padding: 4px !important;
            font-size: 10px !important;
          }
          
          .field-group {
            margin-bottom: 8px !important;
          }
          
          .group-title {
            font-size: 11px !important;
            margin-bottom: 5px !important;
          }
          
          .field-row {
            margin-bottom: 3px !important;
          }
          
          .field-label {
            font-size: 9px !important;
            min-width: 80px !important;
          }
          
          .field-input,
          .field-textarea {
            font-size: 8px !important;
            padding: 2px !important;
          }
          
          .task-table th,
          .task-table td {
            padding: 3px !important;
            font-size: 8px !important;
          }
          
          .task-table td {
            height: 25px !important;
          }
          
          .internal-table th,
          .internal-table td {
            padding: 3px !important;
            font-size: 8px !important;
          }
          
          .internal-table td {
            height: 20px !important;
          }
          
          .signature-box {
            height: 25px !important;
          }
          
          .invoice-status-row,
          .pending-tasks-row,
          .signature-row,
          .internal-section {
            margin: 5px 0 !important;
            padding: 5px !important;
          }
          
          .bottom-table-section {
            margin-top: 10px !important;
            padding-top: 10px !important;
          }
          
          @page {
            size: A4 portrait;
            margin: 0.2in !important;
          }
        }

        /* New styles for layout changes */
        .top-row-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
        }

        .customer-availability-header {
          flex: 1;
          color: #333 !important;
        }

        .assigned-to-header {
          flex: 1;
          text-align: right;
          color: #333 !important;
        }

        .assigned-to-value {
          font-size: 16px !important;
          color: #007bff !important;
          background: #f0f8ff;
          padding: 2px 8px;
          border-radius: 4px;
          border: 1px solid #007bff;
        }

        .availability-option {
          margin-left: 15px;
          font-weight: normal;
          cursor: pointer;
          color: #333 !important;
        }

        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }

        .logo-section {
          flex: 0 0 auto;
        }

        .katze-logo {
          width: 120px;
          height: 48px;
        }

        .title-section {
          flex: 1;
          text-align: center;
        }



        .amc-status-section {
          margin-bottom: 15px;
        }

        .amc-title {
          font-weight: bold;
          margin-bottom: 8px;
          color: #333;
        }

        .amc-options {
          display: flex;
          gap: 20px;
        }

        .amc-option {
          font-weight: normal;
          cursor: pointer;
          color: #333;
        }

        .bottom-two-columns {
          display: flex;
          gap: 20px;
          margin: 20px 0;
        }

        .left-column {
          flex: 1;
        }

        .right-column {
          flex: 1;
        }

        .pending-tasks-row {
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          height: 100%;
        }

        .pending-tasks-row .field-label {
          display: block;
          margin-bottom: 8px;
        }

        .pending-tasks-row .field-input {
          width: 100%;
        }

        .signature-box-container {
          padding: 15px;
          text-align: center;
          height: 100%;
        }

        .signature-box {
          margin-top: 15px;
          height: 60px;
          border: 1px dashed #666;
          border-radius: 4px;
          background: white;
        }



        .job-id-highlight {
          font-size: 18px !important;
          font-weight: bold;
          padding: 6px 14px;
        }

        .bold-phone {
          font-weight: bold !important;
        }

        .field-row-split {
          display: flex;
          gap: 20px;
          margin-bottom: 15px;
        }

        .field-half {
          flex: 1;
        }

        .field-half .field-label {
          display: block;
          width: 100%;
          margin-bottom: 5px;
        }

        .field-half .field-input {
          width: 100%;
        }

        .field-row-triple {
          display: flex;
          gap: 15px;
          margin-bottom: 15px;
        }

        .field-third {
          flex: 1;
        }

        .field-third .field-label {
          display: block;
          width: 100%;
          margin-bottom: 5px;
        }

        .field-third .field-input {
          width: 100%;
        }

        .sr-no-col {
          width: 15% !important;
        }

        .task-col {
          width: 70% !important;
        }

        .remark-col {
          width: 30% !important;
        }

        .invoice-status-col {
          width: 15% !important;
          min-width: 120px;
        }

        .bottom-two-columns {
          display: flex;
          gap: 30px;
          margin: 20px 0;
          align-items: flex-start;
        }

        .left-column {
          flex: 2;
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .right-column {
          flex: 1;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }

        .quote-details-bottom {
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
        }

        .quote-details-bottom .field-label {
          display: block;
          margin-bottom: 8px;
        }

        .quote-details-bottom .field-input {
          width: 100%;
        }

        .pending-tasks-row {
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
        }

        .pending-tasks-row .field-label {
          display: block;
          margin-bottom: 8px;
        }

        .pending-tasks-row .field-input {
          width: 100%;
        }

        .signature-box-container {
          text-align: center;
    
          border-radius: 8px;
          padding: 15px;
          width: 200px;
          background: #fafafa;
        }

        .signature-box {
          width: 100%;
          height: 80px;
          border: 1px dashed #999;
          margin-top: 10px;
          background: white;
        }

        .invoice-status-internal {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 5px;
          max-width: 120px;
        }

        .status-title {
          font-weight: bold;
          font-size: 11px;
          color: #333;
          margin-bottom: 3px;
          text-align: center;
        }

        .status-options-compact {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .status-option-compact {
          padding: 4px 6px;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          background: #f8f9fa;
          cursor: pointer;
          text-align: center;
          font-size: 10px;
          font-weight: 500;
          transition: all 0.2s ease;
          user-select: none;
          white-space: nowrap;
        }

        .status-option-compact:hover {
          background: #e9ecef;
          border-color: #007bff;
          transform: translateY(-1px);
        }

        .status-option-compact.active {
          background: linear-gradient(135deg, #28a745, #20c997);
          color: white;
          border-color: #28a745;
          font-weight: bold;
        }

        .status-option-compact.active:hover {
          background: linear-gradient(135deg, #218838, #1ea085);
        }

        @media print {
          .top-row-header {
            margin-bottom: 8px !important;
            padding: 8px !important;
          }

          .field-row-split {
            gap: 10px !important;
          }

          .bottom-two-columns {
            gap: 15px !important;
            margin: 10px 0 !important;
          }

          .left-column {
            gap: 8px !important;
          }

          .quote-details-bottom,
          .pending-tasks-row {
            padding: 8px !important;
            margin-bottom: 5px !important;
          }

          .quote-details-bottom .field-label,
          .pending-tasks-row .field-label {
            font-size: 9px !important;
            margin-bottom: 3px !important;
          }

          .quote-details-bottom .field-input,
          .pending-tasks-row .field-input {
            font-size: 8px !important;
            padding: 2px !important;
          }

          .signature-box-container {
            width: 150px !important;
            padding: 8px !important;
          }

          .signature-box {
            height: 50px !important;
          }

          .invoice-status-internal {
            padding: 3px !important;
            gap: 2px !important;
            max-width: 80px !important;
          }

          .status-title {
            font-size: 8px !important;
            margin-bottom: 1px !important;
          }

          .status-option-compact {
            padding: 2px 4px !important;
            font-size: 7px !important;
            border-width: 1px !important;
            margin-bottom: 1px !important;
          }

          .status-option-compact.active {
            background: #28a745 !important;
            color: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </>
  );
} 